/**
 * GraphQL Query Definitions
 */

import { gql } from '@apollo/client';

// Fragment definitions for reusability
export const OFFER_FRAGMENT = gql`
  fragment OfferFragment on Offer {
    id
    title
    description
    status
    startDate
    validUntil
    isActive
    createdAt
    updatedAt
    productsCount
    viewsCount
    merchantName
    merchant {
      id
      nameArabic
      nameEnglish
      logoUrl
    }
    cities {
      id
      nameArabic
      nameEnglish
    }
  }
`;

export const PRODUCT_FRAGMENT = gql`
  fragment ProductFragment on Product {
    id
    nameArabic
    nameEnglish
    brand
    category
    imageUrl
    originalImageUrl
    confidence
    createdAt
    updatedAt
    extractedData {
      productName
      brand
      priceOld
      priceNew
      discount
      quantity
      category
    }
    boundingBox {
      x1
      y1
      x2
      y2
    }
  }
`;

export const MERCHANT_FRAGMENT = gql`
  fragment MerchantFragment on Hypermarket {
    id
    nameArabic
    nameEnglish
    description
    logoUrl
    website
    phone
    isActive
    offersCount
    totalProducts
    createdAt
    updatedAt
  }
`;

export const CITY_FRAGMENT = gql`
  fragment CityFragment on City {
    id
    nameArabic
    nameEnglish
    region
    country
    isActive
  }
`;

// Dashboard queries
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats {
    dashboardStats {
      offers {
        totalOffers
        activeOffers
        processingOffers
        draftOffers
        expiredOffers
      }
      products {
        totalProducts
        highConfidenceProducts
        mediumConfidenceProducts
        lowConfidenceProducts
        avgConfidence
      }
      merchants {
        totalMerchants
        activeMerchants
        topMerchants {
          ...MerchantFragment
        }
      }
      lastUpdated
    }
  }
  ${MERCHANT_FRAGMENT}
`;

export const GET_RECENT_OFFERS = gql`
  query GetRecentOffers($pagination: PaginationInput) {
    offers(pagination: $pagination) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const GET_RECENT_PRODUCTS = gql`
  query GetRecentProducts($pagination: PaginationInput) {
    products(pagination: $pagination) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

// Comprehensive dashboard query - single request for all dashboard data
export const GET_DASHBOARD_DATA = gql`
  query GetDashboardData {
    # Dashboard statistics
    dashboardStats {
      offers {
        totalOffers
        activeOffers
        processingOffers
        draftOffers
        expiredOffers
      }
      products {
        totalProducts
        highConfidenceProducts
        mediumConfidenceProducts
        lowConfidenceProducts
        avgConfidence
      }
      merchants {
        totalMerchants
        activeMerchants
        topMerchants {
          ...MerchantFragment
        }
      }
      lastUpdated
    }
    
    # Recent offers (last 5)
    recentOffers: offers(pagination: { limit: 5 }) {
      ...OfferFragment
    }
    
    # Recent products (last 10)
    recentProducts: products(pagination: { limit: 10 }) {
      ...ProductFragment
    }
    
    # All merchants for dropdowns
    merchants {
      ...MerchantFragment
    }
    
    # All cities for dropdowns
    cities {
      ...CityFragment
    }
  }
  ${OFFER_FRAGMENT}
  ${PRODUCT_FRAGMENT}
  ${MERCHANT_FRAGMENT}
  ${CITY_FRAGMENT}
`;

// Analytics queries
export const GET_CATEGORY_PERFORMANCE = gql`
  query GetCategoryPerformance {
    categoryPerformance {
      name
      productCount
      avgConfidence
      avgDiscount
    }
  }
`;

export const GET_MERCHANT_PERFORMANCE = gql`
  query GetMerchantPerformance {
    merchantPerformance {
      merchant {
        ...MerchantFragment
      }
      offersCount
      productsCount
      avgConfidence
      avgDiscount
    }
  }
  ${MERCHANT_FRAGMENT}
`;

// Detailed queries for specific pages
export const GET_OFFERS = gql`
  query GetOffers(
    $filters: OfferFilters
    $pagination: PaginationInput
    $sort: SortInput
  ) {
    offers(filters: $filters, pagination: $pagination, sort: $sort) {
      ...OfferFragment
      products {
        ...ProductFragment
      }
      images {
        id
        originalImage
        thumbnail
        imageType
        processingStatus
        createdAt
      }
    }
  }
  ${OFFER_FRAGMENT}
  ${PRODUCT_FRAGMENT}
`;

export const GET_OFFER_DETAILS = gql`
  query GetOfferDetails($id: ID!) {
    offer(id: $id) {
      ...OfferFragment
      products {
        ...ProductFragment
      }
      images {
        id
        originalImage
        thumbnail
        imageType
        processingStatus
        createdAt
      }
    }
  }
  ${OFFER_FRAGMENT}
  ${PRODUCT_FRAGMENT}
`;

export const GET_PRODUCTS = gql`
  query GetProducts(
    $filters: ProductFilters
    $pagination: PaginationInput
    $sort: SortInput
  ) {
    products(filters: $filters, pagination: $pagination, sort: $sort) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

export const GET_MERCHANTS = gql`
  query GetMerchants($active_only: Boolean = true) {
    merchants(active_only: $active_only) {
      ...MerchantFragment
    }
  }
  ${MERCHANT_FRAGMENT}
`;

export const GET_CITIES = gql`
  query GetCities($active_only: Boolean = true) {
    cities(active_only: $active_only) {
      ...CityFragment
    }
  }
  ${CITY_FRAGMENT}
`;

// Search queries
export const SEARCH_OFFERS = gql`
  query SearchOffers($search: String!, $pagination: PaginationInput) {
    offers(
      filters: { search: $search }
      pagination: $pagination
    ) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const SEARCH_PRODUCTS = gql`
  query SearchProducts($search: String!, $pagination: PaginationInput) {
    products(
      filters: { search: $search }
      pagination: $pagination
    ) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

// Filtered queries for specific use cases
export const GET_ACTIVE_OFFERS = gql`
  query GetActiveOffers($pagination: PaginationInput) {
    offers(
      filters: { status: ACTIVE }
      pagination: $pagination
      sort: { field: "start_date", direction: "DESC" }
    ) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const GET_HIGH_CONFIDENCE_PRODUCTS = gql`
  query GetHighConfidenceProducts($pagination: PaginationInput) {
    products(
      filters: { min_confidence: 0.8 }
      pagination: $pagination
      sort: { field: "confidence_score", direction: "DESC" }
    ) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

export const GET_PRODUCTS_BY_CATEGORY = gql`
  query GetProductsByCategory($category: String!, $pagination: PaginationInput) {
    products(
      filters: { category: $category }
      pagination: $pagination
    ) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

// Advanced Analytics Queries
export const GET_ANALYTICS_OVERVIEW = gql`
  query GetAnalyticsOverview($dateRange: String) {
    analyticsOverview(dateRange: $dateRange) {
      offers {
        total
        active
        processing
        expired
        growthRate
      }
      products {
        total
        highConfidence
        avgConfidence
        avgDiscount
        growthRate
      }
      merchants {
        total
        active
        topPerformers {
          ...MerchantFragment
          offersCount
          productsCount
          avgDiscount
        }
      }
      revenue {
        total
        projectedSavings
        avgOrderValue
      }
    }
  }
  ${MERCHANT_FRAGMENT}
`;

export const GET_TIME_SERIES_DATA = gql`
  query GetTimeSeriesData($dateRange: String!, $granularity: String!) {
    timeSeriesData(dateRange: $dateRange, granularity: $granularity) {
      date
      offers
      products
      views
      confidence
      revenue
    }
  }
`;

export const GET_CATEGORY_ANALYTICS = gql`
  query GetCategoryAnalytics($dateRange: String) {
    categoryAnalytics(dateRange: $dateRange) {
      category
      productCount
      avgPrice
      avgDiscount
      totalSavings
      growthRate
      topProducts {
        ...ProductFragment
      }
    }
  }
  ${PRODUCT_FRAGMENT}
`;

export const GET_MERCHANT_ANALYTICS = gql`
  query GetMerchantAnalytics($dateRange: String) {
    merchantAnalytics(dateRange: $dateRange) {
      merchant {
        ...MerchantFragment
      }
      metrics {
        totalOffers
        activeOffers
        totalProducts
        avgConfidence
        avgDiscount
        totalViews
        conversionRate
      }
      trends {
        date
        offers
        products
        views
      }
      topCategories {
        category
        count
        percentage
      }
    }
  }
  ${MERCHANT_FRAGMENT}
`;

export const GET_CONFIDENCE_DISTRIBUTION = gql`
  query GetConfidenceDistribution {
    confidenceDistribution {
      range
      count
      percentage
      avgProcessingTime
    }
  }
`;

export const GET_PRICE_DISTRIBUTION = gql`
  query GetPriceDistribution($currency: String) {
    priceDistribution(currency: $currency) {
      range
      count
      avgDiscount
      totalProducts
    }
  }
`;

export const GET_GEOGRAPHIC_ANALYTICS = gql`
  query GetGeographicAnalytics {
    geographicAnalytics {
      city {
        ...CityFragment
      }
      metrics {
        totalOffers
        totalProducts
        avgDiscount
        topMerchants {
          ...MerchantFragment
        }
      }
    }
  }
  ${CITY_FRAGMENT}
  ${MERCHANT_FRAGMENT}
`;

export const GET_MERCHANT_OFFERS = gql`
  query GetMerchantOffers($merchant_id: Int!, $pagination: PaginationInput) {
    offers(
      filters: { merchant_id: $merchant_id }
      pagination: $pagination
      sort: { field: "created_at", direction: "DESC" }
    ) {
      ...OfferFragment
      products {
        ...ProductFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
  ${PRODUCT_FRAGMENT}
`;

// Comprehensive Analytics Query - Single request for all analytics data
export const GET_FULL_ANALYTICS = gql`
  query GetFullAnalytics($limit: Int = 10) {
    # Overview statistics
    dashboardStats {
      offers {
        totalOffers
        activeOffers
        processingOffers
        draftOffers
        expiredOffers
      }
      products {
        totalProducts
        highConfidenceProducts
        mediumConfidenceProducts
        lowConfidenceProducts
        avgConfidence
      }
      merchants {
        totalMerchants
        activeMerchants
        topMerchants {
          ...MerchantFragment
        }
      }
      lastUpdated
    }
    
    # Category performance
    categoryPerformance {
      name
      productCount
      avgConfidence
      avgDiscount
    }
    
    # Merchant performance
    merchantPerformance {
      merchant {
        ...MerchantFragment
      }
      offersCount
      productsCount
      avgConfidence
      avgDiscount
    }
    
    # Recent data for trends
    recentOffers: offers(pagination: { limit: $limit }, sort: { field: "created_at", direction: "DESC" }) {
      id
      title
      createdAt
      productsCount
      merchant {
        nameArabic
      }
    }
    
    recentProducts: products(pagination: { limit: $limit }, sort: { field: "created_at", direction: "DESC" }) {
      id
      confidence
      confidenceScore
      createdAt
      extractedData {
        productName
        priceNew
        discount
        category
      }
    }
  }
  ${MERCHANT_FRAGMENT}
`;